import axios from "axios";
import i18n from "../../i18n";
import { PDA_PREFIX } from "../../global/constants";
import { awaitAsyncJob } from "../ws/awaitJobOutcome";
import { watchJobProgress } from "../ws/jobProgressWatch";
import { normalizeWsStatus } from "../ws/wsFrameNormalizer";

// After normalizeWsStatus: INTERRUPTED and ALL_COMPLETED_WITH_ERRORS are terminal too.
const ENDED_JOB_STATUSES = new Set(["COMPLETED", "ALL_COMPLETED", "FAILED", "ALL_FAILED"]);

const FOLLOW_TIMEOUT_MS = 300000;
const RETRY_DELAY_MS = 10000;
const MAX_ATTEMPTS = 30;
// Waiters on the same scenario resubmit at a random point of this window, so N tabs
// do not all hit the endpoint the instant the running job ends.
const RESUBMIT_JITTER_MS = 3000;
// The backend sends the job's last item frame before it marks the job ended in its registry: a
// resubmission at once would still find it RUNNING (one more 409, one more follow).
const RESUBMIT_MIN_DELAY_MS = 500;

/**
 * With the scenario lock on, the backend refuses a regeneration while a
 * SCHEDULE_UPDATE job runs on the scenario: 409 {code: RUN_IN_PROGRESS, jobId, startedBy}.
 * Returns `{ jobId, startedBy }` (either may be null: older backends send no `startedBy`)
 * for that refusal, null for anything else.
 */
export function runInProgressOf(err) {
  const body = err?.response?.data;
  if (err?.response?.status !== 409 || body?.code !== "RUN_IN_PROGRESS") return null;
  const startedBy = typeof body.startedBy === "string" && body.startedBy.trim() ? body.startedBy.trim() : null;
  return { jobId: body.jobId || null, startedBy };
}

/**
 * The message that explains a refusal from putBlockSchedule: its budget ran out while a
 * job still held the scenario, or a 409 RUN_IN_PROGRESS, naming who started the run when the
 * backend says so. Null otherwise.
 */
export function runStateMessage(err) {
  if (err?.runStillInProgress) return i18n.t("scheduleRegeneration.stillRunning");
  const running = runInProgressOf(err);
  if (!running) return null;
  return running.startedBy
    ? i18n.t("scheduleRegeneration.runInProgressStartedBy", { name: running.startedBy })
    : i18n.t("scheduleRegeneration.runInProgress");
}

/**
 * Error bodies are strings on some endpoints and objects on others; a toast
 * renders an object as a React child and throws. Only a string ever goes out.
 */
export function toastableMessage(value, fallback) {
  if (typeof value === "string" && value) return value;
  if (typeof value?.message === "string" && value.message) return value.message;
  return fallback;
}

/**
 * What to show when a regeneration call fails: the "still running" / "already running"
 * wording while a job merely holds the scenario, otherwise the server message (string only)
 * or `fallback`. Null for an aborted wait (the page or the session went away): callers
 * show nothing then.
 */
export function regenerationErrorMessage(err, fallback) {
  if (err?.aborted) return null;
  return runStateMessage(err) ?? toastableMessage(err?.response?.data, fallback);
}

/** The signed-in username, or null (storage blocked or not set). Never "null"/"undefined". */
export function currentUsername() {
  try {
    return localStorage.getItem("username") || null;
  } catch {
    return null;
  }
}

/**
 * The progress frames (ETA, phases) of a scenario's schedule update, for a card whose job is
 * followed by status polling: open it before the request that starts the job.
 */
export function watchScheduleProgress(scenarioId) {
  const username = currentUsername();
  return watchJobProgress({
    topic: username ? `/topic/scenario-update/${username}` : null,
    itemId: scenarioId,
  });
}

/**
 * Follows a SCHEDULE_UPDATE job without starting one. Status polling is armed at once
 * (no 30 s frame silence first; the first read still comes after the 8 s poll interval):
 * when the job is another user's, its frames go to that user's topic and the
 * status answers 403, which settles as "forbidden" instead of waiting for the timeout.
 * Aborting `signal` tears the subscription and the polling down. A WebSocket connection that
 * cannot be opened settles as "timeout": the job's outcome is unknown, not a failure.
 * `onProgress` receives the job's non-terminal frames (phases, ETA) for its progress card.
 * Resolves "completed" | "failed" | "forbidden" | "timeout" | "aborted".
 */
export function followScheduleJob(scenarioId, jobId, { timeoutMs = FOLLOW_TIMEOUT_MS, signal, onProgress } = {}) {
  const username = currentUsername();
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve("aborted");
      return;
    }
    let cleanup = null;
    const onAbort = () => {
      if (typeof cleanup === "function") cleanup();
      resolve("aborted");
    };
    // A long-lived signal outlives many follows: each one takes its listener away.
    const settle = (outcome) => {
      signal?.removeEventListener("abort", onAbort);
      resolve(outcome);
    };
    signal?.addEventListener("abort", onAbort, { once: true });
    cleanup = awaitAsyncJob({
      topic: username ? `/topic/scenario-update/${username}` : null,
      itemId: scenarioId,
      timeoutMs,
      pollDelayMs: 0,
      post: async () => ({ data: { jobId } }),
      onProgress,
      onCompleted: () => settle("completed"),
      onFailed: () => settle("failed"),
      onForbidden: () => settle("forbidden"),
      onTimeout: () => settle("timeout"),
      // The post is synthetic, so this is the WebSocket connection failing (gateway down):
      // the job itself runs on. Saying "failed" would be wrong; its outcome is unknown.
      onRequestError: () => settle("timeout"),
    });
  });
}

/** Waits `ms`, or less when `signal` aborts first. */
function sleep(ms, signal) {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const done = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal?.addEventListener("abort", done, { once: true });
  });
}

/** True only when the status of `jobId` is readable and says the job has not ended. */
async function jobStillRunning(jobId) {
  try {
    const resp = await axios.get(`${PDA_PREFIX}/async-job/${jobId}/status`);
    const status = resp?.data?.status;
    return Boolean(status) && !ENDED_JOB_STATUSES.has(normalizeWsStatus(status));
  } catch {
    return false;
  }
}

/**
 * Sends a regeneration request (`submit`, an axios call) made safe against the
 * scenario lock: on 409 RUN_IN_PROGRESS it follows the running job, then submits
 * again, because that job may predate the change this call has to apply (a new
 * Date of Review, the edits behind an "Update usages"). A job that ended (completed or
 * failed: either way the lock is free) is resubmitted after the jitter only. A job it
 * cannot follow to its end (another user's, timed out) is retried every `retryDelayMs`
 * within `budgetMs` / `maxAttempts` (`maxAttempts` is a test hook); once the budget is
 * spent on such a job, it gives up without another wait or submit.
 * The resubmission after an ended job waits `minResubmitDelayMs` plus the jitter.
 * Before each resubmit the job it waited on is read once more: still running (e.g. a
 * follow that timed out early) means waiting on it, not another refused PUT. Delays
 * carry a random jitter (`jitterMs`) so concurrent waiters do not resubmit together.
 * `signal` aborts the wait, the backoff included: the call then throws an Error with
 * `aborted: true` and submits nothing more. `onWait(jobId, { startedBy })` is called for
 * each job that made the call wait, so a caller listening to the job topic can ignore that
 * job's frames and say who holds the scenario.
 * Throws an Error with `runStillInProgress: true` when the budget runs out, and the
 * original error for anything else (a 409 without a job id included).
 */
export async function submitWhenFree(
  scenarioId,
  submit,
  {
    budgetMs = FOLLOW_TIMEOUT_MS,
    retryDelayMs = RETRY_DELAY_MS,
    maxAttempts = MAX_ATTEMPTS,
    jitterMs = RESUBMIT_JITTER_MS,
    minResubmitDelayMs = RESUBMIT_MIN_DELAY_MS,
    signal,
    onWait,
  } = {}
) {
  const deadline = Date.now() + budgetMs;
  const abortedError = () => Object.assign(new Error("Schedule wait aborted"), { aborted: true });
  const jitter = () => Math.floor(Math.random() * jitterMs);
  const stillRunningError = (jobId) =>
    Object.assign(new Error("Schedule update still in progress"), { runStillInProgress: true, jobId });
  let waitingOn = null;
  for (let attempt = 1; ; attempt++) {
    if (signal?.aborted) throw abortedError();
    try {
      if (waitingOn && (await jobStillRunning(waitingOn))) {
        // Not over yet: follow it again instead of provoking another 409.
        const again = await followScheduleJob(scenarioId, waitingOn, {
          timeoutMs: Math.max(deadline - Date.now(), 1),
          signal,
        });
        if (again === "aborted") throw abortedError();
      }
      waitingOn = null;
      // The abort may have landed during the status read above.
      if (signal?.aborted) throw abortedError();
      return await submit();
    } catch (err) {
      const running = runInProgressOf(err);
      if (err?.aborted || !running?.jobId) throw err;
      if (attempt >= maxAttempts || Date.now() >= deadline) throw stillRunningError(running.jobId);
      onWait?.(running.jobId, { startedBy: running.startedBy });
      const outcome = await followScheduleJob(scenarioId, running.jobId, {
        timeoutMs: Math.max(deadline - Date.now(), 1),
        signal,
      });
      if (outcome === "aborted") throw abortedError();
      const ended = outcome === "completed" || outcome === "failed";
      if (!ended && Date.now() >= deadline) throw stillRunningError(running.jobId);
      waitingOn = ended ? null : running.jobId;
      await sleep(ended ? minResubmitDelayMs + jitter() : retryDelayMs + jitter(), signal);
      if (signal?.aborted) throw abortedError();
    }
  }
}

// One signal per widget: a long-lived Gantt gets one `destroy` listener, not one per action.
const destroySignals = new WeakMap();

/**
 * A signal aborted when the Bryntum `widget` (a Gantt, a grid) is destroyed, i.e. when the page
 * holding it unmounts: a regeneration started from its menus stops waiting for the scenario
 * lock then. Already aborted for a destroyed widget. Shared by every call on the same widget, so
 * a caller that listens to it takes its listener away when it settles.
 */
export function abortOnDestroy(widget) {
  if (widget?.isDestroyed) {
    const destroyed = new AbortController();
    destroyed.abort();
    return destroyed.signal;
  }
  if (widget == null || typeof widget !== "object") return new AbortController().signal;
  const known = destroySignals.get(widget);
  if (known) return known;
  const controller = new AbortController();
  if (typeof widget.on === "function") {
    widget.on({ destroy: () => controller.abort(), once: true });
  }
  destroySignals.set(widget, controller.signal);
  return controller.signal;
}

/** The sync `PUT /scenario/{id}/update-block-schedule`, through submitWhenFree. */
export function putBlockSchedule(scenarioId, options) {
  return submitWhenFree(
    scenarioId,
    () => axios.put(`${PDA_PREFIX}/scenario/${scenarioId}/update-block-schedule`),
    options
  );
}
